import { getApiBaseUrl } from '../config/apiConfig';
import type {
  ApiConfig,
  GetLicenseResponse,
  LicenseData,
  LicenseType,
  LicensingApiClient,
} from '../types/api';
import { isDevelopment } from '../utils/environment';
import { BaseApiClient } from './apiClient';

/**
 * Service for retrieving licensing information
 */
export class LicensingService extends BaseApiClient implements LicensingApiClient {
  constructor(config?: Partial<ApiConfig>) {
    const isDevMode = isDevelopment();

    super({
      baseUrl: getApiBaseUrl(isDevMode),
      ...config,
    });
  }

  /**
   * Get license information for the authenticated user using app endpoint
   * This endpoint uses only token authentication (no request body)
   */
  async getUserLicense(): Promise<GetLicenseResponse> {
    const response = await this.post<GetLicenseResponse>('/licensing/app/license');
    return response.data;
  }

  /**
   * Get license information for the authenticated user using app endpoint
   * This is the main method used throughout the application
   */
  async getLicense(_idToken: string): Promise<GetLicenseResponse> {
    // Use the working /licensing/app/license endpoint with token authentication
    const response = await this.post<GetLicenseResponse>('/licensing/app/license');
    return response.data;
  }

  /**
   * Check if user has a valid license
   */
  async hasValidLicense(idToken: string): Promise<boolean> {
    try {
      const license = await this.getLicense(idToken);
      return license.license_type !== 'null' && license.license_type !== 'anon';
    } catch (_error) {
      return false;
    }
  }

  /**
   * Get license type for the user
   */
  async getLicenseType(idToken: string): Promise<LicenseType> {
    try {
      const license = await this.getLicense(idToken);
      return license.license_type;
    } catch (_error) {
      return 'null';
    }
  }

  /**
   * Get license data for the user
   */
  async getLicenseData(idToken: string): Promise<LicenseData | null> {
    try {
      const license = await this.getLicense(idToken);
      return license.license_data;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Check if user has access to a specific product
   */
  async hasProductAccess(idToken: string, productName: string): Promise<boolean> {
    try {
      const licenseData = await this.getLicenseData(idToken);
      return licenseData?.products.includes(productName) ?? false;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Get limit for a specific license item
   */
  async getItemLimit(idToken: string, itemName: string): Promise<number | null> {
    try {
      const licenseData = await this.getLicenseData(idToken);
      const item = licenseData?.items[itemName];
      return item?.limit || null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Check if an item has a hard stop limit
   */
  async hasHardStopLimit(idToken: string, itemName: string): Promise<boolean> {
    try {
      const licenseData = await this.getLicenseData(idToken);
      const item = licenseData?.items[itemName];
      return item?.hard_stop ?? false;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Get warning percentage for a license item
   */
  async getWarningPercentage(idToken: string, itemName: string): Promise<number | null> {
    try {
      const licenseData = await this.getLicenseData(idToken);
      const item = licenseData?.items[itemName];
      return item?.warn_percent || null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Check if user has paid license
   */
  async hasPaidLicense(idToken: string): Promise<boolean> {
    const licenseType = await this.getLicenseType(idToken);
    return licenseType === 'paid';
  }

  /**
   * Check if user has free license
   */
  async hasFreeLicense(idToken: string): Promise<boolean> {
    const licenseType = await this.getLicenseType(idToken);
    return licenseType === 'free';
  }

  /**
   * Get all available products for the user
   */
  async getAvailableProducts(idToken: string): Promise<string[]> {
    try {
      const licenseData = await this.getLicenseData(idToken);
      return licenseData?.products || [];
    } catch (_error) {
      return [];
    }
  }
}

// Create singleton instance
export const licensingService = new LicensingService();
